import { Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import {
  SquaresFourIcon,
  ChartPieSliceIcon,
  CurrencyDollarIcon,
  ScalesIcon,
  ChartLineUpIcon,
  LinkIcon,
} from '@phosphor-icons/react';
import { useCurrentUser } from '../auth/useCurrentUser';
import { usePlaidAutoSync } from '../data/usePlaidAutoSync';
import { useScope } from '../hooks/useScope';
import { BrandMark } from './BrandMark';
import { ScopeToggle } from './ScopeToggle';

// The topbar's avatar link to Settings — also used by the home screen.
export function ProfileNavButton() {
  const me = useCurrentUser();
  const pictureUrl = me.status === 'ready' && me.mode === 'real' ? me.user.pictureUrl : null;
  const initial =
    me.status === 'ready' && me.mode === 'real'
      ? (me.user.displayName ?? me.user.email).slice(0, 1).toUpperCase()
      : me.status === 'ready' && me.mode === 'demo'
        ? 'D'
        : '?';

  return (
    <NavLink
      to="/settings"
      aria-label="Settings"
      className={({ isActive }) =>
        clsx('topbar-avatar', isActive && 'topbar-avatar-active glow-brand')
      }
    >
      {pictureUrl ? (
        <img src={pictureUrl} alt="" referrerPolicy="no-referrer" />
      ) : (
        <span>{initial}</span>
      )}
    </NavLink>
  );
}

const PRIMARY_ITEMS = [
  { to: '/finance', label: 'Dashboard', Icon: SquaresFourIcon, end: true },
  { to: '/spending', label: 'Spending', Icon: ChartPieSliceIcon, end: false },
  { to: '/income', label: 'Income', Icon: CurrencyDollarIcon, end: false },
  { to: '/net-worth', label: 'Net Worth', Icon: ScalesIcon, end: false },
  { to: '/retirement', label: 'Retire', Icon: ChartLineUpIcon, end: false },
  { to: '/links', label: 'Links', Icon: LinkIcon, end: false },
] as const;

function MobileNav() {
  const navClass = ({ isActive }: { isActive: boolean }) =>
    clsx('mobile-nav-item', isActive && 'mobile-nav-item-active');

  // The anchor is a fixed-position 100dvh wrapper — dvh tracks the visible
  // viewport height across iOS URL-bar collapse/expand, so absolute
  // positioning inside it follows the visible bottom edge without the
  // `position: fixed` re-anchoring quirk on iOS Chrome. `pointer-events:
  // none` on the anchor lets clicks pass through everywhere except on
  // the bar itself.
  return (
    <div className="mobile-nav-anchor" aria-hidden={false}>
      <nav className="mobile-nav" aria-label="Mobile navigation">
        {PRIMARY_ITEMS.map(({ to, label, Icon, end }) => (
          <NavLink key={to} to={to} className={navClass} end={end}>
            <Icon size={20} weight="bold" />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

// Routes whose contents read from the global ScopeProvider. On other routes
// (Settings, the user/household Rules toggle) the toggle is rendered but
// hidden via `visibility` so the topbar layout doesn't shift on navigation
// — the slot stays in place, just invisible and non-interactive.
const SCOPED_ROUTES = new Set<string>([
  '/finance',
  '/net-worth',
  '/income',
  '/spending',
  '/retirement',
  '/links',
]);

export function Layout() {
  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    clsx('nav-item', isActive && 'nav-item-active');
  usePlaidAutoSync();
  const location = useLocation();
  const { scope, setScope } = useScope();
  const showScope = SCOPED_ROUTES.has(location.pathname);

  return (
    <div className="app-layout">
      <div className="app-topbar-anchor">
        <header className="app-topbar">
          <Link to="/" className="brand" aria-label="Berg home">
            <BrandMark className="brand-wordmark h-6 w-auto" />
            <BrandMark className="brand-monogram h-6 w-auto" variant="monogram" />
          </Link>

          <nav className="nav-list">
            <NavLink to="/finance" className={navLinkClass} end>
              Dashboard
            </NavLink>
            <NavLink to="/spending" className={navLinkClass}>
              Spending
            </NavLink>
            <NavLink to="/income" className={navLinkClass}>
              Income
            </NavLink>
            <NavLink to="/net-worth" className={navLinkClass}>
              Net Worth
            </NavLink>
            <NavLink to="/retirement" className={navLinkClass}>
              Retirement
            </NavLink>
            <NavLink to="/links" className={navLinkClass}>
              Links
            </NavLink>
          </nav>

          <div
            // Always-mounted slot so the topbar layout doesn't shift when the
            // active route doesn't read scope. `visibility: hidden` keeps the
            // box in flow (preserving the space-between layout) while also
            // making the buttons non-interactive and removing them from a11y.
            aria-hidden={!showScope}
            style={{ visibility: showScope ? 'visible' : 'hidden' }}
          >
            <ScopeToggle value={scope} onChange={setScope} />
          </div>

          <div className="topbar-actions">
            <ProfileNavButton />
          </div>
        </header>
      </div>

      <main className="app-main">
        <Outlet />
      </main>

      <MobileNav />
    </div>
  );
}
